"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SectionReveal } from "./SectionReveal";
import { CarFront, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<{ outcome: "accepted" | "dismissed" }>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export function IKDownloadApp() {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [installResult, setInstallResult] = useState<"accepted" | "dismissed" | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as { standalone?: boolean }).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (isInstalled) return;

    const deferred = deferredPromptRef.current;
    if (deferred) {
      try {
        await deferred.prompt();
        const { outcome } = await deferred.userChoice;
        setInstallResult(outcome);
        if (outcome === "accepted") {
          deferredPromptRef.current = null;
          setIsInstalled(true);
        }
      } catch {
        setShowInstructions(true);
      }
    } else {
      setShowInstructions(true);
    }
  };

  const isIOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as unknown as { MSStream?: boolean }).MSStream;

  return (
    <SectionReveal
      className="relative py-8 sm:py-10 md:py-12 lg:py-16"
      aria-labelledby="download-app-heading"
    >
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.4, ease: [0.22, 0.61, 0.36, 1] }}
          className="max-w-2xl mx-auto rounded-2xl border border-neutral-200/60 dark:border-neutral-700/60 bg-white dark:bg-neutral-900/50 shadow-lg p-8 md:p-10 flex flex-col items-center text-center"
        >
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#FFB51D]/10 text-[#FFB51D]">
            <CarFront className="w-7 h-7" strokeWidth={2} aria-hidden />
          </div>
          <h2
            id="download-app-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#131222] dark:text-white mb-4"
          >
            IK Travels on the Go!
          </h2>
          <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 mb-6">
            Install our Progressive Web App for a faster and smoother experience.
          </p>

          {isInstalled ? (
            <p className="font-medium text-[#FFB51D] text-lg">
              You&apos;re using the app!
            </p>
          ) : installResult === "accepted" ? (
            <p className="font-medium text-green-600 dark:text-green-500 text-lg">
              Install started. Check your home screen.
            </p>
          ) : (
            <>
              <Button
                onClick={handleInstall}
                className="bg-gradient-to-r from-[#FFB51D] to-[#FFA500] hover:from-[#FFA500] hover:to-[#FFB51D] text-[#131222] font-bold px-6 py-3 rounded-xl shadow-lg"
                aria-label="Install app or add to home screen"
              >
                <Download className="w-5 h-5 mr-2" aria-hidden />
                Add to Home Screen
              </Button>

              {showInstructions && (
                <div className="mt-6 w-full text-left rounded-xl bg-[#FFB51D]/10 dark:bg-[#FFB51D]/20 border border-[#FFB51D]/30 p-4">
                  <p className="text-sm font-medium text-[#131222] dark:text-white mb-2">
                    How to add:
                  </p>
                  {isIOS ? (
                    <ol className="text-sm text-neutral-700 dark:text-neutral-300 space-y-1 list-decimal list-inside">
                      <li>Tap the Share button (square with arrow)</li>
                      <li>Scroll and tap &quot;Add to Home Screen&quot;</li>
                      <li>Tap &quot;Add&quot;</li>
                    </ol>
                  ) : (
                    <p className="text-sm text-neutral-700 dark:text-neutral-300">
                      Open the browser menu (⋮) → &quot;Install app&quot; or &quot;Add to Home screen&quot;
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowInstructions(false)}
                    className="mt-2 text-sm text-[#FFB51D] hover:underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </SectionReveal>
  );
}
